const STRING_FIELDS = new Set([
    'birth_date',
    'country_of_residence',
    'firstname',
    'gender',
    'lastname',
    'middlename',
    'nationality',
    'region_of_residence',
    'residence_since',
    'residency_status',
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/** Omit empty strings and boolean-false on string columns so they do not persist as junk. */
export const sanitizeProfileFields = <T extends Record<string, unknown>>(payload: T): T => {
    const next: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(payload)) {
        if (value === undefined) {
            continue;
        }
        if (value === '') {
            continue;
        }
        if (value === false && STRING_FIELDS.has(key)) {
            continue;
        }
        next[key] = value;
    }
    return next as T;
};

export interface SpouseProfileFields {
    firstname?: string | null;
    lastname?: string | null;
    middlename?: string | null;
    birth_date?: string | Date | null;
    gender?: string | null;
    nationality?: string | null;
    country_of_residence?: string | null;
    region_of_residence?: string | null;
    residency_status?: string | null;
    residence_since?: string | Date | null;
    income?: string | number | null;
}

export interface SplitSpouseProfilePayload {
    user: Record<string, unknown>;
    personalInfo: Record<string, unknown>;
    citizenships: Array<{ country: string }>;
}

/** Names on User; dates/residence/income on PersonalInfo; nationality as Citizenship. */
export const splitSpouseProfilePayload = (
    spouse: SpouseProfileFields
): SplitSpouseProfilePayload => {
    const user = sanitizeProfileFields({
        firstname: spouse.firstname ?? null,
        lastname: spouse.lastname ?? null,
        middlename: spouse.middlename ?? null,
    });
    const personalInfo = sanitizeProfileFields({
        birth_date: spouse.birth_date ?? null,
        gender: spouse.gender ?? null,
        country_of_residence: spouse.country_of_residence ?? null,
        region_of_residence: spouse.region_of_residence ?? null,
        residency_status: spouse.residency_status ?? null,
        residence_since: spouse.residence_since ?? null,
        income: spouse.income === '' || spouse.income == null ? null : spouse.income,
    });
    const nationality = typeof spouse.nationality === 'string' ? spouse.nationality.trim() : '';
    return {
        user,
        personalInfo,
        citizenships: nationality ? [{ country: nationality }] : [],
    };
};

const firstCitizenshipCountry = (value: unknown): string | null => {
    if (typeof value === 'string' && value.trim()) {
        return value.trim();
    }
    if (Array.isArray(value)) {
        for (const item of value) {
            const country = firstCitizenshipCountry(item);
            if (country) {
                return country;
            }
        }
        return null;
    }
    if (isRecord(value)) {
        return firstCitizenshipCountry(
            value.country ?? value.name ?? value.nationality ?? value.Citizenship
        );
    }
    return null;
};

export const mergeSpouseUserTables = (
    user: unknown,
    personalInfo: unknown,
    citizenships: unknown
): SpouseProfileFields => {
    const userRecord = isRecord(user) ? user : {};
    const personalRecord = isRecord(personalInfo) ? personalInfo : {};
    const nationality =
        firstCitizenshipCountry(citizenships) ??
        (typeof personalRecord.nationality === 'string' ? personalRecord.nationality : null);

    return {
        firstname: (userRecord.firstname as string | null) ?? null,
        lastname: (userRecord.lastname as string | null) ?? null,
        middlename: (userRecord.middlename as string | null) ?? null,
        birth_date: (personalRecord.birth_date as string | Date | null) ?? null,
        gender: (personalRecord.gender as string | null) ?? null,
        nationality,
        country_of_residence: (personalRecord.country_of_residence as string | null) ?? null,
        region_of_residence: (personalRecord.region_of_residence as string | null) ?? null,
        residency_status: (personalRecord.residency_status as string | null) ?? null,
        residence_since: (personalRecord.residence_since as string | Date | null) ?? null,
        income: (personalRecord.income as string | number | null) ?? null,
    };
};
